<template>
  <div class="page-container financial-statements-page">
    <portal to="page-name">Extrato</portal>
    <div class="card card-page">
      <div class="card-header">
        <button class="btn btn-primary btn-icon-left float-right"
                v-if="canImportBankStatement"
                :disabled="!filter.bankAccountId"
                @click="openImport">
          <fa-icon :icon="['fal', 'upload']"></fa-icon>Importar OFX
        </button>
        <h1 class="card-title">Extrato</h1>
      </div>
      <div class="card-body">
        <div class="columns">
          <div class="column col-auto col-md-6 col-sm-12 form-group">
            <label class="form-label">Conta</label>
            <select class="form-select" v-model="filter.bankAccountId">
              <option value="">[Selecione uma conta]</option>
              <option v-for="item in bankAccounts"
                      :value="item.id" :key="item.id">{{ item.name }}</option>
            </select>
          </div>
          <div class="column col-4 col-md-6 col-sm-12 form-group"
               :class="{'has-error': $v.filter.startDate.$error || $v.filter.endDate.$error}">
            <label class="form-label">Período</label>
            <st-date-period
              :start-date.sync="filter.startDate"
              :end-date.sync="filter.endDate"
              :start-date-validator="$v.filter.startDate"
              :end-date-validator="$v.filter.endDate"
              @blur:startDate="$v.filter.startDate.$touch()"
              @blur:endDate="$v.filter.endDate.$touch()"
            />
          </div>
          <div class="column col-md-12 col-sm-12 form-group">
            <label class="form-label">Pesquisar</label>
            <input type="text" class="form-input"
                   v-model="filter.search"
                   placeholder="Nome ou documento...">
          </div>
        </div>
        <div class="columns">
          <div class="column col-auto col-sm-12 form-group">
            <label class="form-label">Visão</label>
            <!-- Alterna a variante em vez de usar '.active': o .active do
                 Spectre usa $primary-color cru, enquanto os botões do projeto
                 usam $primary-color-light. Padrão de
                 surgery-appointment/components/ContentPage.vue:51 -->
            <div class="btn-group btn-group-block">
              <button class="btn"
                      :class="view === 'system' ? 'btn-primary' : 'btn-gray'"
                      @click="setView('system')">Sistema</button>
              <button class="btn"
                      :class="view === 'bank' ? 'btn-primary' : 'btn-gray'"
                      @click="setView('bank')">Banco</button>
            </div>
          </div>
        </div>
        <div class="loading loading-lg" v-if="loading" />
        <template v-else-if="view === 'system'">
          <div class="empty mt-2" v-if="data.items.length === 0">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
            </div>
            <p class="empty-title h5">Extrato</p>
            <p class="empty-subtitle">
              Nenhuma movimentação encontrada. Verifique os filtros para realizar novas buscas
            </p>
          </div>
          <table class="table table-striped table-hover" v-else>
            <thead>
            <tr>
              <th style="width: 80px">Data</th>
              <th class="text-center" style="width: 70px">Status</th>
              <th>Lançamento</th>
              <th>Categoria</th>
              <th class="text-right">Valor</th>
              <th class="text-right">Saldo</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, i) in data.items" :key="i">
              <td>{{ item.date | date }}</td>
              <td class="text-center">
                <span class="tooltip"
                      :class="reconciliationStatus(item).color"
                      :data-tooltip="reconciliationStatus(item).label">
                  <fa-icon :icon="['fal', reconciliationStatus(item).icon]" />
                </span>
              </td>
              <td>{{ item.entity.name }}</td>
              <td>{{ item.category.name }}</td>
              <td class="text-right"
                  :class="item.total < 0 ? 'text-error' : 'text-success'">
                {{ item.total | currency }}
              </td>
              <td class="text-right">{{ item.balance | currency }}</td>
            </tr>
            </tbody>
          </table>
        </template>
        <template v-else>
          <div class="empty mt-2" v-if="bankData.items.length === 0">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'university']" size="3x"/>
            </div>
            <p class="empty-title h5">Extrato do banco</p>
            <p class="empty-subtitle">
              Nenhum lançamento importado para esta conta no período.
              Importe o arquivo OFX do seu banco para começar.
            </p>
            <button class="btn btn-primary btn-icon-left"
                    v-if="canImportBankStatement"
                    :disabled="!filter.bankAccountId"
                    @click="openImport">
              <fa-icon :icon="['fal', 'upload']"></fa-icon>Importar OFX
            </button>
          </div>
          <template v-else>
            <div class="reconcile-bar mb-2" v-if="canReconcile">
              <div class="reconcile-summary">
                <strong class="reconcile-percent">{{ bankProgress.percent }}%</strong>
                <span>
                  <strong>{{ bankProgress.matched }}</strong> de
                  <strong>{{ bankProgress.total }}</strong> conciliados
                </span>
                <span v-if="autoSuggestions.length > 0">
                  <strong class="text-primary">{{ autoSuggestions.length }}</strong>
                  {{ autoSuggestions.length === 1 ? 'sugestão' : 'sugestões' }}
                  de alta confiança
                </span>
              </div>
              <button class="btn btn-primary btn-sm"
                      :disabled="suggesting || autoSuggestions.length === 0"
                      :class="{loading: suggesting}"
                      @click="applyAutoSuggestions">
                Conciliar automático
              </button>
            </div>
            <table class="table table-striped table-hover">
              <thead>
              <tr>
                <th style="width: 80px">Data</th>
                <th class="text-center" style="width: 70px">Status</th>
                <th>Lançamento</th>
                <th style="width: 120px">Documento</th>
                <th class="text-right" style="width: 140px">Valor</th>
                <th style="width: 70px"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in bankData.items" :key="item.id">
                <td>{{ item.date | date('date', true) }}</td>
                <td class="text-center">
                  <span class="tooltip"
                        :class="bankStatus(item).color"
                        :data-tooltip="bankStatus(item).label">
                    <fa-icon :icon="['fal', bankStatus(item).icon]" />
                  </span>
                </td>
                <td>
                  <div>{{ item.description || '-' }}</div>
                  <div class="statement-match" v-if="suggestionFor(item)">
                    <fa-icon :icon="['fal', 'lightbulb']" class="text-warning"></fa-icon>
                    <strong class="match-name"
                            :title="suggestionName(suggestionFor(item))">
                      {{ suggestionName(suggestionFor(item)) }}
                    </strong>
                    <span class="match-value">
                      {{ suggestionFor(item).transaction.value | currency }}
                    </span>
                    <span class="label label-gray tooltip tooltip-left"
                          :data-tooltip="scoreTooltip(suggestionFor(item))">
                      {{ suggestionFor(item).score }}%
                    </span>
                  </div>
                </td>
                <td>{{ item.document || '-' }}</td>
                <td class="text-right"
                    :class="item.signedValue < 0 ? 'text-error' : 'text-success'">
                  {{ item.signedValue | currency }}
                </td>
                <td class="text-right">
                  <!-- Um botão primário por status, sempre exatamente um: é a
                       contagem constante que mantém a coluna alinhada. Mesmo
                       padrão de billing/transaction/components/Main.vue. -->
                  <button class="btn btn-sm btn-action btn-icon mr-1
                                 btn-success tooltip tooltip-left"
                          v-if="suggestionFor(item)"
                          :disabled="!canReconcile || confirming === item.id"
                          :class="{loading: confirming === item.id}"
                          :data-tooltip="primaryTooltip(item)"
                          @click="confirmSuggestion(suggestionFor(item))">
                    <fa-icon :icon="['fal', 'check']"></fa-icon>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon mr-1
                                 btn-secondary tooltip tooltip-left"
                          v-else-if="item.status === 'pending'"
                          :disabled="!canReconcile"
                          :data-tooltip="primaryTooltip(item)"
                          @click="openReconcile(item)">
                    <fa-icon :icon="['fal', 'link']"></fa-icon>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon mr-1
                                 btn-neutral tooltip tooltip-left"
                          v-else-if="item.status === 'matched'"
                          :disabled="!canUndoReconciliation"
                          :data-tooltip="primaryTooltip(item)"
                          @click="undoReconciliation(item)">
                    <fa-icon :icon="['fal', 'undo']"></fa-icon>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon mr-1
                                 btn-neutral tooltip tooltip-left"
                          v-else
                          :disabled="!canUpdateBankStatement"
                          :data-tooltip="primaryTooltip(item)"
                          @click="setStatementStatus(item, 'pending')">
                    <fa-icon :icon="['fal', 'eye']"></fa-icon>
                  </button>

                  <!-- Sempre renderizado, desabilitado quando não há
                       alternativas: esconder faria a coluna variar de largura.
                       'right' alinha o menu à direita (cresce para a esquerda);
                       é o único valor que o componente reconhece. -->
                  <dx-dropdown class="row-menu"
                               :items="rowActions(item)"
                               :disabled="rowActions(item).length === 0"
                               label="name"
                               direction="right"
                               @select="action => runRowAction(action, item)">
                    <button class="btn btn-sm btn-action btn-icon
                                   btn-gray tooltip tooltip-left"
                            :disabled="rowActions(item).length === 0"
                            data-tooltip="Mais ações">
                      <fa-icon :icon="['fal', 'bars']"></fa-icon>
                    </button>
                  </dx-dropdown>
                </td>
              </tr>
              </tbody>
            </table>
            <div class="mt-2">
              <st-pagination :total="bankData.total"
                             :limit="bankData.limit"
                             @paginate="paginateBank"></st-pagination>
            </div>
          </template>
        </template>
      </div>
      <div class="card-footer text-right" v-if="view === 'system'">
        <div class="mr-1">
          <div class="form-group">
            Saldo inicial:
            <span class="text-info">{{ bankAccountBalance | currency }}</span>
          </div>
          <div class="form-group">
            Total de recebimentos:
            <span class="text-success">{{ total.revenueValue | currency }}</span>
          </div>
          <div class="form-group">
            Total de pagamentos:
            <span class="text-error">{{ total.expenseValue | currency }}</span>
          </div>
          <div class="form-group">
            Saldo final: <strong>{{ total.finalValue | currency }}</strong>
          </div>
          <div class="form-group" v-if="ledgerBalance">
            Saldo do banco ({{ ledgerBalance.date | date('date', true) }}):
            <span class="text-info">{{ ledgerBalance.value | currency }}</span>
          </div>
          <div class="form-group" v-if="ledgerBalance">
            Diferença:
            <strong :class="difference === 0 ? 'text-success' : 'text-error'">
              {{ difference | currency }}
            </strong>
          </div>
        </div>
      </div>
      <div class="card-footer text-right" v-else>
        <div class="mr-1">
          <div class="form-group">
            Total de créditos:
            <span class="text-success">{{ bankTotal.credit | currency }}</span>
          </div>
          <div class="form-group">
            Total de débitos:
            <span class="text-error">{{ bankTotal.debit | currency }}</span>
          </div>
          <div class="form-group">
            Lançamentos: <strong>{{ bankData.total }}</strong>
          </div>
        </div>
      </div>
    </div>

    <reconcile-modal v-if="reconcileStatement"
                     :initial-mode="reconcileMode"
                     :statement="reconcileStatement"
                     @close="reconcileStatement = null"
                     @saved="onReconciled" />

    <dx-modal v-model="importModal.show"
              title="Importar extrato (OFX)"
              id="bank-statement-import-modal"
              size="lg">
      <div class="columns">
        <div class="column col-12 form-group">
          <label class="form-label">Conta financeira</label>
          <input type="text" class="form-input text-bold"
                 v-model="importModal.bankAccountName" readonly>
        </div>
      </div>

      <template v-if="importModal.step === 'file'">
        <div class="columns">
          <div class="column col-12 form-group">
            <div class="upload-area"
                 :class="{dragging: importModal.dragging}">
              <input type="file" class="input-file" accept=".ofx,.OFX"
                     ref="fileInput"
                     @dragenter="importModal.dragging = true"
                     @dragleave="importModal.dragging = false"
                     @drop="importModal.dragging = false"
                     @change="uploadChange">
              <div class="upload-inner">
                <fa-icon :icon="['fal', 'upload']"></fa-icon>
                <p v-if="importModal.file">{{ importModal.file.name }}</p>
                <p v-else>Arraste o arquivo OFX do banco aqui</p>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="importModal.step === 'review'">
        <div class="toast toast-warning mb-2" v-if="accountMismatch">
          <fa-icon :icon="['fal', 'exclamation-triangle']" class="mr-1"></fa-icon>
          O arquivo é da conta <strong>{{ importModal.review.ofx.accountId }}</strong>
          do banco <strong>{{ importModal.review.ofx.bankId }}</strong>.
          Confira se corresponde à conta selecionada antes de importar.
        </div>
        <table class="table">
          <tbody>
          <tr>
            <td class="text-bold">Banco / conta no arquivo</td>
            <td class="text-right">
              {{ importModal.review.ofx.bankId || '-' }} /
              {{ importModal.review.ofx.accountId || '-' }}
            </td>
          </tr>
          <tr>
            <td class="text-bold">Período</td>
            <td class="text-right">
              {{ importModal.review.period.startDate | date('date', true) }} a
              {{ importModal.review.period.endDate | date('date', true) }}
            </td>
          </tr>
          <tr>
            <td class="text-bold">Saldo final no arquivo</td>
            <td class="text-right">
              {{ importModal.review.ofx.ledgerBalance.value | currency }}
            </td>
          </tr>
          <tr>
            <td class="text-bold">Lançamentos no arquivo</td>
            <td class="text-right">{{ importModal.review.summary.total }}</td>
          </tr>
          <tr>
            <td class="text-bold">Serão importados</td>
            <td class="text-right text-success text-bold">
              {{ importModal.review.summary.imported }}
            </td>
          </tr>
          <tr v-if="importModal.review.summary.duplicated > 0">
            <td class="text-bold">Já existem (serão ignorados)</td>
            <td class="text-right">{{ importModal.review.summary.duplicated }}</td>
          </tr>
          </tbody>
        </table>
      </template>

      <template v-else>
        <div class="empty">
          <div class="empty-icon">
            <fa-icon :icon="['fal', 'check-circle']" size="3x" class="text-success"/>
          </div>
          <p class="empty-title h5">Importação concluída</p>
          <p class="empty-subtitle">
            {{ importModal.result.imported }} lançamento(s) importado(s).
            <template v-if="importModal.result.duplicated > 0">
              {{ importModal.result.duplicated }} já existia(m) e foi(ram) ignorado(s).
            </template>
          </p>
        </div>
      </template>

      <template slot="footer">
        <button class="btn mr-1" @click="closeImport">
          {{ importModal.step === 'done' ? 'Fechar' : 'Cancelar' }}
        </button>
        <button class="btn btn-primary"
                v-if="importModal.step === 'file'"
                :disabled="!importModal.file || importModal.saving"
                :class="{loading: importModal.saving}"
                @click="reviewImport">Conferir</button>
        <button class="btn btn-primary"
                v-else-if="importModal.step === 'review'"
                :disabled="importModal.saving || importModal.review.summary.imported === 0"
                :class="{loading: importModal.saving}"
                @click="confirmImport">Importar</button>
      </template>
    </dx-modal>
  </div>
</template>

<script>
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import {
  IMPORT_BANK_STATEMENT,
  UPDATE_BANK_STATEMENT,
  RECONCILE_BANK_ACCOUNT,
  UNDO_BANK_RECONCILIATION,
} from '@/data/actions/modules/financial';
import ReconcileModal from './Reconcile.vue';
import { date, minDate } from '../../../../data/validators';

const blankImportModal = () => ({
  show: false,
  step: 'file',
  bankAccountName: '',
  dragging: false,
  saving: false,
  file: null,
  review: null,
  result: null,
});

export default {
  components: { ReconcileModal },
  data() {
    return {
      path: '/financial-statements',
      bankPath: '/bank-statements',
      loading: false,
      view: 'system',
      bankAccounts: [],
      bankAccountBalance: 0,
      ledgerBalance: null,
      filter: {
        bankAccountId: '',
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        search: '',
      },
      data: {
        items: [],
        total: 0,
        limit: 30,
        offset: 0,
      },
      bankData: {
        items: [],
        total: 0,
        limit: 30,
        offset: 0,
      },
      total: {
        revenueValue: 0,
        expenseValue: 0,
        finalValue: 0,
      },
      importModal: blankImportModal(),
      reconcileStatement: null,
      reconcileMode: 'match',
      suggestions: [],
      discarded: [],
      suggesting: false,
      confirming: null,
    };
  },
  computed: {
    canImportBankStatement() {
      return this.$can(IMPORT_BANK_STATEMENT);
    },
    canUpdateBankStatement() {
      return this.$can(UPDATE_BANK_STATEMENT);
    },
    canReconcile() {
      return this.$can(RECONCILE_BANK_ACCOUNT);
    },
    canUndoReconciliation() {
      return this.$can(UNDO_BANK_RECONCILIATION);
    },
    /**
     * Score >= 90 é aplicado em lote sem perguntar. Abaixo disso o usuário
     * confirma linha a linha.
     */
    autoSuggestions() {
      return this.suggestions.filter(
        item => item.auto && !this.discarded.includes(item.statement.id),
      );
    },
    bankProgress() {
      const total = this.bankData.total || 0;
      const matched = this.bankData.items
        .filter(({ status }) => status === 'matched').length;
      return {
        total,
        matched,
        percent: total > 0 ? Math.round((matched / total) * 100) : 0,
      };
    },
    bankTotal() {
      return this.bankData.items.reduce((acc, item) => {
        if (item.type === 'credit') {
          acc.credit += item.value;
        } else {
          acc.debit += item.value;
        }
        return acc;
      }, { credit: 0, debit: 0 });
    },
    difference() {
      if (!this.ledgerBalance) {
        return 0;
      }
      return Math.round((this.total.finalValue - this.ledgerBalance.value) * 100) / 100;
    },
    accountMismatch() {
      const { review } = this.importModal;
      if (!review || !review.ofx) {
        return false;
      }
      // Sem o vínculo OFX gravado na conta não dá para afirmar divergência;
      // o alerta serve para o usuário conferir, não para bloquear.
      return !!review.ofx.accountId;
    },
  },
  mounted() {
    this.loadBankAccounts();
  },
  watch: {
    filter: {
      handler() {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(this.load, 800);
      },
      deep: true,
    },
  },
  validations() {
    const rules = {
      filter: {
        bankAccountId: { required },
        startDate: { required, date },
        endDate: { required, date },
      },
    };

    if (this.filter.startDate) {
      rules.filter.endDate = {
        required,
        date,
        minDate: minDate(this.filter.startDate),
      };
    }

    return rules;
  },
  methods: {
    setView(view) {
      if (this.view === view) {
        return;
      }
      this.view = view;
      this.load();
    },
    reconciliationStatus(item) {
      return item.reconciliation && item.reconciliation.status === 'done'
        ? { icon: 'lock', color: 'text-success', label: 'Conciliado' }
        : { icon: 'lock-open', color: 'text-error', label: 'Não conciliado' };
    },
    bankStatus(item) {
      switch (item.status) {
        case 'matched':
          return { icon: 'lock', color: 'text-success', label: 'Conciliado' };
        case 'partial':
          return { icon: 'adjust', color: 'text-warning', label: 'Parcialmente conciliado' };
        case 'ignored':
          return { icon: 'ban', color: 'text-gray', label: 'Ignorado' };
        default:
          return { icon: 'lock-open', color: 'text-error', label: 'Não conciliado' };
      }
    },
    loadBankAccounts() {
      const params = {
        active: true,
        limit: 0,
        cashier: false,
      };
      return this.$http.get('/bank-accounts', { params })
        .then(({ data }) => {
          this.bankAccounts = data.items;
        })
        .catch(() => {
        });
    },
    paginateBank({ offset }) {
      this.bankData.offset = offset;
      this.loadBankStatements();
    },
    /**
     * Grau de compatibilidade, não probabilidade: é a soma ponderada de valor
     * (50), data (30), nome/documento (15) e histórico (5). O tooltip diz de
     * onde vem o número, para não ser lido como "chance de estar certo".
     */
    scoreTooltip(suggestion) {
      return suggestion.reasons.join(' · ');
    },
    /**
     * Com ícone no lugar do rótulo, o tooltip é quem carrega a informação que o
     * texto do botão dava — e o motivo, quando a ação está indisponível.
     */
    primaryTooltip(item) {
      const suggestion = this.suggestionFor(item);

      if (suggestion) {
        return this.canReconcile
          ? `Aceitar sugestão: ${this.suggestionName(suggestion)}`
          : 'Sem permissão para conciliar';
      }

      switch (item.status) {
        case 'matched':
          return this.canUndoReconciliation
            ? 'Desfazer conciliação'
            : 'Sem permissão para desfazer';
        case 'ignored':
          return this.canUpdateBankStatement
            ? 'Reativar lançamento'
            : 'Sem permissão para reativar';
        default:
          return this.canReconcile
            ? 'Conciliar manualmente'
            : 'Sem permissão para conciliar';
      }
    },
    suggestionName(suggestion) {
      return suggestion.transaction.entity
        ? suggestion.transaction.entity.name
        : 'Lançamento sem favorecido';
    },
    /**
     * Ações secundárias do menu. A principal fica no botão ao lado — aqui só
     * entra o que é alternativa ou exceção, para a linha não virar um painel.
     */
    rowActions(item) {
      const actions = [];

      // 'Desfazer' é a única ação de uma linha conciliada e vira botão direto,
      // por isso não entra no menu.
      if (item.status === 'matched') {
        return actions;
      }

      if (item.status === 'ignored') {
        return actions;
      }

      if (this.canReconcile) {
        if (this.suggestionFor(item)) {
          actions.push({ code: 'match', name: 'Escolher outro lançamento' });
          actions.push({ code: 'discard', name: 'Descartar sugestão' });
        }
        actions.push({ code: 'create', name: 'Criar lançamento' });
      }

      if (this.canUpdateBankStatement) {
        actions.push({ code: 'ignore', name: 'Ignorar lançamento' });
      }

      return actions;
    },
    runRowAction(action, item) {
      switch (action.code) {
        case 'match':
          this.openReconcile(item, 'match');
          break;
        case 'create':
          this.openReconcile(item, 'create');
          break;
        case 'discard':
          this.discardSuggestion(item);
          break;
        case 'ignore':
          this.setStatementStatus(item, 'ignored');
          break;
        default:
          break;
      }
    },
    /**
     * Reflete o novo estado da linha sem ir ao servidor.
     *
     * É seguro para ações que CONSOMEM um lançamento (conciliar, criar,
     * ignorar): a atribuição do matcher é gulosa e 1:1, então cada sugestão usa
     * um lançamento exclusivo — remover um par não destrava sugestão para
     * nenhuma outra linha. Ações que LIBERAM (desfazer, reativar) devevolvem o
     * lançamento ao conjunto disponível e podem gerar sugestões novas, então
     * essas recarregam de verdade.
     *
     * bankProgress é computed sobre bankData.items, logo o contador acompanha.
     */
    /**
     * Desfazer libera o lançamento de volta ao conjunto. A linha volta a
     * pendente localmente (sem recarregar a lista), mas as sugestões precisam
     * ser recalculadas no servidor: o lançamento liberado pode virar sugestão
     * de OUTRA linha, e isso o cliente não tem como derivar — ao contrário das
     * ações que consomem, onde a atribuição 1:1 do matcher garante segurança.
     */
    applyUndoLocal(statementId) {
      const item = this.bankData.items.find(({ id }) => id === statementId);

      if (item) {
        this.$set(item, 'status', 'pending');
        this.$set(item, 'reconciledValue', 0);
      }

      return this.loadSuggestions();
    },
    applyLocalStatus(statementId, status) {
      const item = this.bankData.items.find(({ id }) => id === statementId);

      if (item) {
        this.$set(item, 'status', status);
        if (status === 'matched') {
          this.$set(item, 'reconciledValue', Math.abs(item.signedValue));
        }
      }

      this.suggestions = this.suggestions
        .filter(({ statement }) => statement.id !== statementId);
    },
    suggestionFor(item) {
      if (item.status !== 'pending' || this.discarded.includes(item.id)) {
        return null;
      }
      return this.suggestions.find(({ statement }) => statement.id === item.id) || null;
    },
    discardSuggestion(item) {
      this.discarded.push(item.id);
    },
    loadSuggestions() {
      if (!this.canReconcile || !this.filter.bankAccountId) {
        return Promise.resolve();
      }

      const params = {
        bankAccountId: this.filter.bankAccountId,
        startDate: this.filter.startDate,
        endDate: this.filter.endDate,
      };

      return this.$http.get('/bank-reconciliations/suggestions', { params })
        .then(({ data }) => {
          this.suggestions = data.items;
        })
        .catch(() => {
          // sugestão é conveniência: falhar aqui não pode atrapalhar o extrato
          this.suggestions = [];
        });
    },
    payloadOf(suggestion) {
      return {
        statementIds: [suggestion.statement.id],
        transactions: [{
          id: suggestion.transaction.id,
          paymentIndex: suggestion.transaction.paymentIndex,
        }],
        score: suggestion.score,
      };
    },
    confirmSuggestion(suggestion) {
      this.confirming = suggestion.statement.id;

      return this.$http.post('/bank-reconciliations', this.payloadOf(suggestion))
        .then(() => {
          this.$toast.show('Lançamento conciliado com sucesso!');
          this.applyLocalStatus(suggestion.statement.id, 'matched');
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.confirming = null;
        });
    },
    /**
     * Aplica várias conciliações de uma vez, então confirma antes: o usuário
     * precisa saber quantos lançamentos serão afetados e que é reversível.
     */
    applyAutoSuggestions() {
      const total = this.autoSuggestions.length;

      if (total === 0) {
        return;
      }

      const plural = total === 1 ? 'lançamento será conciliado' : 'lançamentos serão conciliados';

      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + `<div>${total} ${plural} automaticamente com os `
          + 'lançamentos do sistema de alta compatibilidade.</div>'
          + '<div class="mt-2 text-gray">Os lançamentos conciliados não poderão ser '
          + 'alterados. Você pode desfazer cada conciliação individualmente.</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary ml-2',
            click: (close) => {
              this.suggesting = true;

              this.$http
                .post('/bank-reconciliations/batch', {
                  items: this.autoSuggestions.map(this.payloadOf),
                })
                .then(({ data }) => {
                  if (data.failed && data.failed.length > 0) {
                    this.$toast.show(
                      `${data.applied} conciliado(s), `
                      + `${data.failed.length} não puderam ser aplicados.`,
                      { type: 'warning' },
                    );
                  } else {
                    this.$toast.show(`${data.applied} lançamento(s) conciliado(s) com sucesso!`);
                  }
                  this.loadBankStatements();
                })
                .catch((e) => {
                  this.$toast.error(e);
                })
                .finally(() => {
                  this.suggesting = false;
                  close();
                });
            },
          },
        ],
      });
    },
    openReconcile(item, mode = 'match') {
      this.reconcileMode = mode;
      this.reconcileStatement = item;
    },
    onReconciled(statementId) {
      this.reconcileStatement = null;
      this.applyLocalStatus(statementId, 'matched');
    },
    setStatementStatus(item, status) {
      return this.$http.put(`${this.bankPath}/${item.id}`, { status })
        .then(() => {
          // 'ignored' consome a linha; 'pending' (reativar) a devolve ao
          // conjunto e pode gerar sugestão nova — só esse caso recarrega.
          if (status === 'ignored') {
            this.applyLocalStatus(item.id, 'ignored');
          } else {
            this.loadBankStatements();
          }
        })
        .catch((e) => {
          this.$toast.error(e);
        });
    },
    undoReconciliation(item) {
      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente desfazer a conciliação deste lançamento?</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              // A linha não guarda o id do vínculo; busca-se pelo statementId,
              // que é a chave que o usuário tem em mãos.
              this.$http
                .get('/bank-reconciliations', {
                  params: { statementId: item.id, limit: 1 },
                })
                .then(({ data }) => {
                  if (!data.items || data.items.length === 0) {
                    throw new Error('Conciliação não encontrada.');
                  }
                  return this.$http.delete(`/bank-reconciliations/${data.items[0].id}`);
                })
                .then(() => {
                  this.$toast.show('Conciliação desfeita com sucesso!');
                  this.applyUndoLocal(item.id);
                })
                .catch((e) => {
                  let errorMessage = 'Ocorreu um erro ao realizar a operação solicitada!';
                  if (e.response) {
                    if (e.response.status === 403) {
                      errorMessage = 'Acesso não autorizado';
                    } else if (e.response.data && e.response.data.message) {
                      errorMessage = e.response.data.message;
                    }
                  }
                  this.$toast.show(errorMessage, { type: 'error' });
                })
                .finally(() => {
                  close();
                });
            },
          },
        ],
      });
    },
    openImport() {
      const bankAccount = this.bankAccounts
        .find(({ id }) => id === this.filter.bankAccountId);

      this.importModal = blankImportModal();
      this.importModal.bankAccountName = bankAccount ? bankAccount.name : '';
      this.importModal.show = true;
    },
    closeImport() {
      const imported = this.importModal.step === 'done';
      this.importModal = blankImportModal();

      if (imported) {
        this.load();
      }
    },
    uploadChange(e) {
      e.preventDefault();
      const { files } = e.target;
      this.importModal.file = files && files.length ? files[0] : null;
    },
    buildImportPayload(dryRun) {
      const formData = new FormData();
      formData.append('file', this.importModal.file);
      formData.append('bankAccountId', this.filter.bankAccountId);
      formData.append('dryRun', dryRun ? 'true' : 'false');
      return formData;
    },
    handleImportError(e) {
      this.$toast.error(e);
    },
    reviewImport() {
      this.importModal.saving = true;

      this.$http.post(`${this.bankPath}/import`, this.buildImportPayload(true))
        .then(({ data }) => {
          this.importModal.review = data;
          this.importModal.step = 'review';
        })
        .catch(this.handleImportError)
        .finally(() => {
          this.importModal.saving = false;
        });
    },
    confirmImport() {
      this.importModal.saving = true;

      this.$http.post(`${this.bankPath}/import`, this.buildImportPayload(false))
        .then(({ data }) => {
          this.importModal.result = data.summary;
          this.importModal.step = 'done';
          this.$toast.show('Extrato importado com sucesso!');
        })
        .catch(this.handleImportError)
        .finally(() => {
          this.importModal.saving = false;
        });
    },
    load() {
      this.$v.filter.$touch();
      if (this.$v.filter.$error) {
        return Promise.resolve();
      }

      return this.view === 'bank'
        ? this.loadBankStatements()
        : this.loadSystemStatements();
    },
    loadBankStatements() {
      this.loading = true;

      const params = {
        bankAccountId: this.filter.bankAccountId,
        startDate: this.filter.startDate,
        endDate: this.filter.endDate,
        offset: this.bankData.offset,
        limit: this.bankData.limit,
      };

      if (this.filter.search) {
        params.search = this.filter.search;
      }

      return this.$http.get(this.bankPath, { params })
        .then(({ data }) => {
          this.bankData = data;
          return this.loadSuggestions();
        })
        .catch(() => {
          this.$toast.error('Não foi possível carregar o extrato do banco.');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async loadSystemStatements() {
      this.loading = true;

      const params = {
        startDate: this.filter.startDate,
        endDate: this.filter.endDate,
        bankAccountId: this.filter.bankAccountId,
      };

      if (this.filter.search) {
        params.search = this.filter.search;
      }

      await this.getAccountInitialValue(params);
      await this.loadLedgerBalance();

      return this.$http.get(this.path, { params })
        .then(({ data }) => {
          this.data = data;
          let currentBalance = this.bankAccountBalance;
          this.data.items = this.data.items.map((item) => {
            currentBalance += item.total;
            item.balance = currentBalance;
            return item;
          });
        })
        .catch(() => {
          this.$toast.error('Não foi possível carregar o extrato.');
        })
        .finally(() => {
          this.calcTotal();
          this.loading = false;
        });
    },
    /**
     * Saldo declarado pelo banco na última importação que cobre o período —
     * é o número que o contador compara com o saldo final do sistema.
     */
    loadLedgerBalance() {
      this.ledgerBalance = null;

      const params = {
        bankAccountId: this.filter.bankAccountId,
        limit: 1,
      };

      return this.$http.get('/bank-statement-imports', { params })
        .then(({ data }) => {
          const last = data.items && data.items.length ? data.items[0] : null;
          if (last && last.ofx && last.ofx.ledgerBalance) {
            this.ledgerBalance = last.ofx.ledgerBalance;
          }
        })
        .catch(() => {
        });
    },
    calcTotal() {
      this.total.revenueValue = this.data.items
        .filter(({ type }) => type === 'revenue')
        .reduce((a, b) => a + b.total, 0);
      this.total.expenseValue = this.data.items
        .filter(({ type }) => type === 'expense')
        .reduce((a, b) => a + b.total, 0);
      this.total.finalValue = this.data.items.length > 0
        ? this.data.items[this.data.items.length - 1].balance
        : this.bankAccountBalance;
    },
    getAccountInitialValue(item) {
      const params = {
        date: item.startDate,
      };

      if (item.bankAccountId) {
        params.id = item.bankAccountId;
      }

      return this.$http
        .get('/bank-account-balance', { params })
        .then(({ data }) => {
          this.bankAccountBalance = data && data.balance ? data.balance : 0;
        })
        .catch(() => {
        });
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";

.financial-statements-page {
  // Espaçamento uniforme entre os blocos: antes eram ml-1/ml-2 avulsos mais um
  // separador solto, o que dava ritmo irregular. baseline alinha o percentual,
  // que tem corpo maior, com o texto ao lado.
  .reconcile-summary {
    align-items: baseline;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-lg;
  }
  .reconcile-percent {
    font-size: $font-size-lg;
  }
  .reconcile-bar {
    align-items: center;
    // separa do seletor de visão (Sistema|Banco) logo acima
    margin-top: $layout-spacing-lg;
    background-color: lighten($gray-color-light, 5%);
    border-radius: $border-radius;
    display: flex;
    justify-content: space-between;
    padding: $layout-spacing $layout-spacing-lg;
  }
  // A sugestão é um bloco ANEXADO à linha, não uma segunda linha de texto: a
  // barra à esquerda e o fundo próprio fazem ela ler como anotação do
  // lançamento acima. Inline-flex para ocupar só a largura do conteúdo — tingir
  // a linha inteira espalhava cor por seis colunas sem informação nenhuma.
  .statement-match {
    align-items: center;
    // o tom mais claro da paleta ainda pesava no fundo; diluído em branco
    background-color: mix($yellow-color-100, #fff, 45%);
    border-left: $border-width-lg solid $yellow-color-400;
    border-radius: 0 $border-radius $border-radius 0;
    font-size: $font-size-sm;
    margin-top: $unit-1;
    padding: $unit-1 $unit-2;
    // Grid de colunas fixas: sem isto cada bloco dimensiona pelo próprio
    // conteúdo e nada alinha entre as linhas. O minmax(0, ...) é o que permite
    // truncar — sem ele o conteúdo força a coluna a crescer.
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) 4.5rem auto;
    column-gap: $unit-2;
    .label {
      font-size: $font-size-sm;
      white-space: nowrap;
    }
  }
  // Truncar em vez de quebrar mantém toda sugestão com uma linha de altura;
  // o texto completo fica no title.
  .match-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .match-value {
    text-align: right;
    white-space: nowrap;
  }
  // O tamanho do trigger vem de btn-action (_buttons.scss); aqui só se corrige
  // a tipografia do menu, que não herda o 0.6rem imposto por _tables.scss.
  .row-menu {
    // _tables.scss impõe 0.6rem em th/td, mas o .menu do Spectre traz o próprio
    // tamanho — sem isto o menu fica desproporcional dentro da tabela.
    .menu {
      font-size: $font-size;
      text-align: left;
      white-space: nowrap;
      .menu-item > a {
        padding: $unit-1 $unit-2;
      }
    }
  }
}

#bank-statement-import-modal {
  .upload-area {
    align-items: center;
    background-color: lighten($gray-color-light, 5%);
    border: lighten($gray-color, 5%) $unit-h dashed;
    display: flex;
    height: 4rem;
    justify-content: center;
    margin-top: $layout-spacing;
    position: relative;
    &.dragging {
      border-color: $primary-color;
    }
    .input-file {
      display: block;
      height: 100%;
      left: 0;
      opacity: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }
    .upload-inner {
      display: flex;
      align-items: center;
    }
    svg {
      font-size: 1rem;
    }
    p {
      margin: 0 0 0 $layout-spacing;
      text-align: center;
    }
  }
}
</style>
